import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { VideoHero, FeatureGrid, ContentSection, SEOContent, CTASection } from '../components/shared';
import { ArrowRight, Calendar, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';

const homeFeatures = [
  {
    id: '2A1',
    title: 'Akute Beschwerden',
    description: 'Schnelle Hilfe bei plötzlichen Beschwerden in unserer Akutsprechstunde.',
    image: '/images/Akutsprechstunden_EKG.webp',
    link: '/leistungen/hausarzt/akut'
  },
  {
    id: '2A',
    title: 'Hausarzt & Familie',
    description: 'Umfassende Betreuung für die ganze Familie.',
    image: '/images/2A_Hausarzt_Familie.webp',
    link: '/leistungen/hausarzt'
  },
  {
    id: '2B',
    title: 'Diagnostik vor Ort',
    description: 'Modernste Technik für präzise Diagnosen.',
    image: '/images/MRT 5.webp',
    link: '/leistungen/diagnostik'
  },
  {
    id: '2C',
    title: 'Herz & Gefäße',
    description: 'Spezialisierte Vorsorge für Ihr Herz.',
    image: '/images/2C_Hero_Kardiologie.webp',
    link: '/leistungen/kardiologie'
  },
  {
    id: '2D',
    title: 'Vorsorge',
    description: 'Gesundheit bewahren durch Früherkennung.',
    image: '/images/ganze Familie.webp',
    link: '/leistungen/vorsorge'
  },
  {
    id: '2H',
    title: 'Patienten-Service',
    description: 'Online-Services für Ihren Komfort.',
    image: '/images/4_Termin_Online.webp',
    link: '/leistungen/digital'
  }
];

export const HomePage = () => {
  return (
    <Layout>
      {/* Video Hero - Fullscreen */}
      <VideoHero />

      {/* Hero Content Section - UNTER dem Video */}
      <section className="py-16 md:py-20 bg-[#032925] text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-[#edd68d] mb-6 leading-tight">
            Gemeinschaftspraxis<br />
            <span className="text-white">Dres. von Bergh</span><br />
            im Medicum Diez
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-3xl mx-auto">
            Ihre Hausärzte für Diez, Limburg und den Rhein-Lahn-Kreis. Persönliche Medizin, modernste Diagnostik und 21 Fachärzte unter einem Dach.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/termin">
              <Button size="lg" className="bg-[#edd68d] hover:bg-[#d4c17d] text-[#032925] font-semibold px-8 py-6 text-base rounded-full shadow-lg">
                <Calendar className="w-5 h-5 mr-2" />
                Jetzt Termin vereinbaren
              </Button>
            </Link>
            <Link to="/leistungen/hausarzt/akut">
              <Button
                size="lg"
                className="border-2 !border-white/80 !bg-transparent !text-white hover:!border-[#edd68d] hover:!bg-[#edd68d] hover:!text-[#032925] [&_svg]:text-current font-semibold px-8 py-6 text-base rounded-full shadow-none transition-colors duration-200"
              >
                <Phone className="w-5 h-5 mr-2" />
                Akut-Hilfe & Kontakt
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 1.2 Feature Grid - "Wie können wir Ihnen helfen?" */}
      <FeatureGrid
        title="Wie können wir Ihnen helfen?"
        features={homeFeatures}
        columns={3}
      />

      {/* 1.3 Content - Medizin ohne Umwege */}
      <ContentSection
        title="Medizin ohne Umwege – willkommen im Medicum Diez"
        content={
          <div>
            <p className="text-[#032925]/80 leading-relaxed mb-4">
              In unserer Gemeinschaftspraxis Dres. von Bergh verbinden wir medizinische Kompetenz mit persönlicher Zuwendung. Als Ihre ersten Ansprechpartner begleiten wir Sie vom ersten Gespräch bis zur gezielten Diagnostik – ruhig, verständlich und mit Zeit für Ihre Fragen.
            </p>
            <Link to="/ueber-uns/medicum" className="inline-flex items-center gap-2 text-[#032925] hover:text-[#1a1308] font-medium transition-colors">
              Das Medicum-Konzept
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
        image="/images/Medizin_aus_Ueberzeugung.webp"
        imagePosition="right"
        backgroundColor="white"
      />

      {/* 1.4 Content - Das Medicum-Konzept */}
      <ContentSection
        title="Das Medicum-Konzept – Medizin als Teamarbeit"
        content={
          <div>
            <p className="text-[#032925]/80 leading-relaxed mb-4">
              Bei uns arbeiten Hausärzte und Fachärzte eng zusammen. Befunde werden gemeinsam besprochen, Wege bleiben kurz und Entscheidungen transparent – damit Sie sich gut aufgehoben fühlen.
            </p>
            <Link to="/ueber-uns/medicum" className="inline-flex items-center gap-2 text-[#032925] hover:text-[#1a1308] font-medium transition-colors">
              Das Medicum-Konzept
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
        image="/images/Das_Medicum_Prinzip.webp"
        imagePosition="left"
        backgroundColor="gray"
      />

      {/* 1.5 Content - Offenes MRT */}
      <ContentSection
        title="Offenes MRT in Diez"
        content={
          <div>
            <p className="text-[#032925]/80 leading-relaxed mb-4">
              Moderne MRT-Untersuchungen in ruhiger Atmosphäre – auch bei Platzangst und in enger fachlicher Abstimmung mit der Kardiologie.
            </p>
            <Link to="/leistungen/diagnostik/mrt" className="inline-flex items-center gap-2 text-[#032925] hover:text-[#1a1308] font-medium transition-colors">
              Offenes MRT
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
        image="/images/MRT 5.webp"
        imagePosition="right"
        backgroundColor="white"
      />

      {/* 1.6 Content - Hausarzt aus Überzeugung */}
      <ContentSection
        title="Hausarzt aus Überzeugung"
        content={
          <div>
            <p className="text-[#032925]/80 leading-relaxed mb-4">
              Dr. Martin von Bergh führt die Praxis gemeinsam mit seiner Familie. Uns verbindet der Anspruch, Medizin menschlich, verständlich und verantwortungsvoll zu gestalten.
            </p>
            <Link to="/ueber-uns/kompass" className="inline-flex items-center gap-2 text-[#032925] hover:text-[#1a1308] font-medium transition-colors">
              Unser Kompass
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
        image="/images/Hausarzt_Team.webp"
        imagePosition="left"
        backgroundColor="gray"
      />

      {/* 1.7 Content - So finden Sie uns */}
      <ContentSection
        title="So finden Sie uns"
        content={
          <div>
            <p className="text-[#032925]/80 leading-relaxed mb-4">
              Medicum Diez – Im Werkes 1, 65582 Diez. Parkplätze direkt am Gebäude. Barrierefrei erreichbar.
            </p>
            <Link to="/termin" className="inline-flex items-center gap-2 text-[#032925] hover:text-[#1a1308] font-medium transition-colors">
              Kontakt & Anfahrt
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        }
        image="/images/So_finden_Sie_uns.webp"
        imagePosition="right"
        backgroundColor="white"
      />

      {/* 1.8 SEO Content */}
      <SEOContent
        title="Ihr Hausarzt in Diez und Limburg"
        content="Als Gemeinschaftspraxis Dres. von Bergh im Medicum Diez sind wir Ihre erste Adresse für hausärztliche Versorgung in Diez, Limburg und im Rhein-Lahn-Kreis. Wir bieten Allgemeinmedizin, Vorsorgeuntersuchungen, moderne Diagnostik und kardiologische Abklärung an einem Standort. Durch die enge Zusammenarbeit mit über 21 Fachärzten im Medicum erhalten Sie schnelle Befunde, kurze Wege und eine koordinierte Behandlung."
      />

      {/* 1.9 CTA - Schnell zum Termin */}
      <CTASection
        title="Schnell zum Termin"
        subtitle="Vereinbaren Sie Ihren Termin online oder kontaktieren Sie uns bei akuten Beschwerden direkt."
        buttonText="Jetzt Termin vereinbaren"
        buttonLink="/termin"
        secondaryButtonText="Akut-Hilfe & Kontakt"
        secondaryButtonLink="/leistungen/hausarzt/akut"
      />
    </Layout>
  );
};

export default HomePage;
